import React, { useState, useEffect, useRef } from 'react'
import { Field } from 'redux-form'
import { FieldLayoutBox, FieldLayoutSizes } from './FieldLayoutBox'
import { FieldValueFormatter, FieldValueParser } from './FieldValueConverters'
import { Input } from 'reactstrap'
import _ from 'common-tools/LodashHelper'
import { ah, ReduxWrapper, compose, QueryWrapper } from 'common-data/datahelpers'
import { Row } from './Row'
import { Col } from './Col'
import styles from './TextAreaLookupBox.less'
import { FAIcon } from './FAIcon'
import { DebounceInput } from 'react-debounce-input'
import Skeleton from 'react-loading-skeleton'
import insertTextAtCursor from 'insert-text-at-cursor'
import clsx from 'clsx'

export const SelectModes = {
  Append: 'A',
  Prepend: 'P',
  None: 'N',
  Both: 'B'
}

// pulled and altered from https://stackoverflow.com/questions/29652862/highlight-text-using-reactjs
function Highlighter ({ children, highlight, onClick }) {
  if (!highlight) {
    return <div onClick={onClick} className="highlighter">{children}</div>
  }

  const regexp = new RegExp(highlight, 'g')
  const matches = children.match(regexp)
  const parts = children.split(new RegExp(`${highlight.replace()}`, 'g'))

  for (let i = 0; i < parts.length; i++) {
    if (i !== parts.length - 1) {
      let match = matches[i]
      // While the next part is an empty string, merge the corresponding match with the current
      // match into a single <span/> to avoid consequent spans with nothing between them.
      while (parts[i + 1] === '') {
        match += matches[++i]
      }

      parts[i] = (
        <React.Fragment key={i}>
          {parts[i]}<span className="highlighted">{match}</span>
        </React.Fragment>
      )
    }
  }
  return <div onClick={onClick} className="highlighter">{parts}</div>
}

function TextLookupResult (props) {
  const { data, searchText, loading = false, onClick, index, size } = props

  if (loading === true || !data.Text) {
    return <div>
      <h5><Skeleton width='40%' /></h5>
      <p>
        <Skeleton height='3%' />
      </p>
    </div>
  }

  const handleOnClick = () => {
    if (_.isFunction(onClick)) {
      onClick(data.Text || '')
    }
  }

  return <div onClick={handleOnClick} className={styles.listGroupItem}>
    <h5><Highlighter highlight={searchText}>{data.Name}</Highlighter> ({data.Group})</h5>
    <p className={`${styles.textResults}`}>
      <Highlighter highlight={searchText} >{data.Text || ''}</Highlighter>
    </p>
    {size - 1 === index ? null : <hr/>}
  </div>
}

function TextNoResult (props) {
  return <div>
    <p>
      Sorry, could not find any matches.  Try searching for something else.
    </p>
  </div>
}

function TextAreaLookupBoxClassPlain (props) {
  const { SearchGroups = [], rows = 4, type,
    input = {}, SearchLookups, formShowSubmissionErrors,
    data: { loadingSearchLookups = false }, maxAutoScroll = 300,
    delayTimeOutMS = 200, delayMinLength = 2, staticMode = false,
    prepend, append, selectMode = SelectModes.None, ...rest } = props
  const { onChange, onBlur, value } = input

  const txtRef = useRef(null)
  const searchRef = useRef(null)

  // options up/down
  const [optionsOpen, setOptionsOpen] = useState(false)
  const handleModeChange = (open = false) => (e) => {
    setOptionsOpen(open)
  }

  // static mode setup
  const [searchStaticData, setSearchStaticData] = useState([])

  useEffect(() => {
    if (staticMode === true) {
      SearchLookups({
        Groups: SearchGroups,
        Text: '*'
      }).then(({ data }) => {
        setSearchStaticData(data)
        setSearchResults(data)
      }).catch(formShowSubmissionErrors)
    } else {
      setSearchStaticData([])
    }
  }, [staticMode])

  // search text input
  const [searchText, setSearchText] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const handleSearchTextChange = (e) => {
    setSearchText(e.target.value)
  }

  useEffect(() => {
    if (optionsOpen === false) {
      setSearchText('')
      if (staticMode === false) {
        setSearchResults([])
      }
    } else {
      if (searchRef.current) {
        searchRef.current.focus()
      }
    }
  }, [optionsOpen])

  useEffect(() => {
    if (searchText !== '') {
      if (staticMode === true) {
        const lowerSearchText = _.toLower(searchText)
        setSearchResults(_.filter(searchStaticData, (a) => {
          return _.includes(_.toLower(a.Text), lowerSearchText) || _.includes(_.toLower(a.Name), lowerSearchText)
        }))
      } else {
        SearchLookups({
          Groups: SearchGroups,
          Text: searchText
        }).then(({ data }) => {
          setSearchResults(data)
        }).catch(formShowSubmissionErrors)
      }
    } else {
      if (staticMode === true) {
        setSearchResults(searchStaticData)
      } else {
        setSearchResults([])
      }
    }
  }, [searchText, staticMode])

  // search result on select

  const handleResultSelect = (text) => {
    if (txtRef.current) {
      let txt = text
      switch (selectMode) {
        case SelectModes.Append:
          txt = `${text}${append || '\n\n'}`
          break
        case SelectModes.Prepend:
          txt = `${prepend || ', '}${text}`
          break
        case SelectModes.Both:
          txt = `${prepend || ', '}${text}${append || '\n\n'}`
          break
      }
      insertTextAtCursor(txtRef.current, txt)
      txtRef.current.style.height = 'inherit'
      txtRef.current.style.height = `${Math.min(txtRef.current.scrollHeight, maxAutoScroll)}px`
      txtRef.current.focus()
    }
  }

  const loadingResults = [<TextLookupResult key='l1' loading={true} />, <TextLookupResult key='l2' loading={true} />, <TextLookupResult key='l3' loading={true} />]

  return (
    <FieldLayoutBox {...rest} input={input}>
      {({ testId, showError, name, placeHolder, loading, readonly, groupLabel }) => {
        return <Row paddingX>
          <Col layout={FieldLayoutSizes.Full}>
            <div className={clsx('filterbar')}>
              <Row className={clsx('align-middle')}>
                <Col layout={FieldLayoutSizes.Full}>
                  <div className="input-group">
                    <Input className={clsx('zero-margin')} data-tid={testId} innerRef={txtRef} readOnly={readonly} type={'textarea'} rows={rows || 4}
                      invalid={showError} name={`textlookuparea${name}`} id={`textlookuparea${name}`}
                      onBlur={onBlur} placeholder={placeHolder} onChange={onChange} value={value} />
                    <span id={clsx(styles['lookup-group-button'])} className={clsx('input-group-label')}>
                      <FAIcon placement='right'
                        toolTip={optionsOpen ? '' : 'Find Quick Text'}
                        name={optionsOpen ? 'arrow-alt-circle-up' : 'search'}
                        onClick={handleModeChange(!optionsOpen)} />
                    </span>
                  </div>
                </Col>
              </Row>
              <Row hide={optionsOpen === false} className={clsx('align-middle')}>
                <Col layout={FieldLayoutSizes.Full}>
                  <DebounceInput
                    inputRef={searchRef}
                    className='form-control'
                    minLength={delayMinLength}
                    debounceTimeout={delayTimeOutMS}
                    placeholder='Find Quick Text'
                    onChange={handleSearchTextChange}
                    value={searchText} />
                </Col>
              </Row>
              <Row hide={optionsOpen === false} className={clsx('align-middle', styles['listResults'])} layout={FieldLayoutSizes.Full}>
                <Col layout={FieldLayoutSizes.Full}>
                  <div className={clsx('search-term-box', styles['listgroup'])}>
                    {loadingSearchLookups
                      ? searchResults.length > 0
                        ? _.concat(_.map(searchResults, (a, i) => (<TextLookupResult onClick={handleResultSelect} key={a._key} index={i} size={searchResults.length} data={a} searchText={searchText} />)), <TextLookupResult key='l0' loading={true} />)
                        : loadingResults
                      : searchResults.length > 0
                        ? _.map(searchResults, (a, i) => (<TextLookupResult onClick={handleResultSelect} index={i} size={searchResults.length} data={a} searchText={searchText || ''} />))
                        : (searchText || '').length > 0
                          ? <TextNoResult />
                          : null
                    }
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      }}
    </FieldLayoutBox>
  )
};

const TextAreaLookupBoxClass = compose(
  ReduxWrapper([

  ], [
    ah.formSetFieldValue, ah.formShowSubmissionErrors
  ]),
  QueryWrapper({
    key: 'TextAreaLookup',
    cleanOnUnmount: true,
    actions: [
      {
        url: '/Services/Data/Lookups/TextArea',
        prop: 'SearchLookups',
        map: false,
        cancelPreviousCall: true
      }
    ]
  })
)(TextAreaLookupBoxClassPlain)

export const TextAreaLookupBox = ({ parse, format, fieldType, ...rest }) => { return <Field parse={fieldType ? FieldValueParser(fieldType) : parse} format={fieldType ? FieldValueFormatter(fieldType) : format} {...rest} component={TextAreaLookupBoxClass} /> } // eslint-disable-line
export default TextAreaLookupBox
